import React from "react";
import {
  CheckCircle2, FileText, AlertCircle, Plus, ArrowRight, X,
  Upload, ThumbsUp, RotateCcw, Trash2,
} from "lucide-react";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumSelect } from "../brand/PremiumSelect";
import { LIGHT, cardStyle, initialsOf } from "./RoomFlow";
import { useOperationalSignals } from "../../hooks/useOperationalSignals";
import { SolicitorResponsivenessPill } from "../operational/SignalPills";

/* ─── Shared atoms ──────────────────────────────────────────────── */
const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-[13.5px] font-bold tracking-tight" style={{ color: LIGHT.textPrimary }}>
      {children}
    </h3>
    {action}
  </div>
);

const _t = (a) => (typeof a === "number" ? a : a ? Date.parse(a) : 0);
const fmtRel = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - _t(iso);
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/* ─── Responsiveness Pill ───────────────────────────────────────── */
const RESP_META = {
  active:     { dot: "#10B981", label: "Active",     pulse: true,  text: "#15803D" },
  responsive: { dot: "#22C55E", label: "Responsive", pulse: false, text: "#15803D" },
  slow:       { dot: "#F59E0B", label: "Slow",       pulse: false, text: "#A16207" },
  silent:     { dot: "#94A3B8", label: "Silent",     pulse: false, text: "#64748B" },
};

const ResponsivenessPill = ({ responsiveness }) => {
  const meta = RESP_META[responsiveness] || RESP_META.silent;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] tabular-nums" style={{ color: meta.text }}>
      <span
        className="w-1.5 h-1.5 rounded-full inline-block"
        style={{ background: meta.dot, boxShadow: meta.pulse ? `0 0 0 3px ${meta.dot}33` : "none" }}
      />
      {meta.label}
    </span>
  );
};

/* ─── Enhanced Participants card (presence + responsiveness + tasks) ─ */
export const ParticipantsLiveCard = ({ participants = [], onInvite, onAssign, dealId }) => {
  const activeCount = participants.filter((p) => p.responsiveness === "active").length;
  // Embedded operational signals — solicitor responsiveness only.
  // Cached per dealId so this is shared with PropertyHeader.
  const { data: opSignals } = useOperationalSignals(dealId);
  const sigByContact = React.useMemo(() => {
    const map = new Map();
    for (const s of opSignals?.solicitors || []) {
      if (s.contact_id) map.set(s.contact_id, s.signal);
    }
    return map;
  }, [opSignals]);
  const sigByRole = React.useMemo(() => {
    const map = new Map();
    for (const s of opSignals?.solicitors || []) {
      if (s.role) map.set(s.role, s.signal);
    }
    return map;
  }, [opSignals]);

  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-participants-live">
      <SectionTitle
        action={
          <span className="inline-flex items-center gap-1.5">
            {activeCount > 0 && (
              <span className="text-[10.5px] font-bold inline-flex items-center gap-1" style={{ color: "#10B981" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#10B981", boxShadow: "0 0 0 3px rgba(16,185,129,0.25)" }} />
                {activeCount} active
              </span>
            )}
            {onInvite && (
              <button
                type="button"
                onClick={onInvite}
                className="text-[10.5px] font-semibold inline-flex items-center gap-0.5"
                style={{ color: "#6366F1" }}
                data-testid="dealroom-participants-invite"
              >
                <Plus size={10} /> Invite
              </button>
            )}
          </span>
        }
      >
        Participants
      </SectionTitle>
      {participants.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-[11.5px]" style={{ color: LIGHT.textSubtle }}>No participants yet.</p>
          {onInvite && (
            <button
              type="button"
              onClick={onInvite}
              className="mt-2 h-8 px-3 rounded-md text-[11.5px] font-semibold inline-flex items-center gap-1"
              style={{ background: "#EEF2FF", color: "#6366F1", border: "1px solid rgba(99,102,241,0.20)" }}
            >
              <Plus size={11} /> Invite first participant
            </button>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {participants.slice(0, 6).map((p, i) => {
            const meta = RESP_META[p.responsiveness] || RESP_META.silent;
            const role = String(p.role || "").toLowerCase();
            const isSolicitor = role === "buyer_solicitor" || role === "seller_solicitor";
            const solSig = isSolicitor
              ? (sigByContact.get(p.user_id) || sigByContact.get(p.id) || sigByRole.get(role))
              : null;
            return (
              <li
                key={p.id || p.user_id || i}
                className="flex items-center gap-2.5"
                data-testid={`dealroom-participant-${p.user_id || p.id}`}
              >
                <span
                  className="relative w-9 h-9 rounded-full text-white text-[10.5px] font-bold flex items-center justify-center shrink-0"
                  style={{ background: `linear-gradient(135deg, hsl(${(i * 67) % 360},48%,55%), hsl(${(i * 67 + 50) % 360},48%,38%))` }}
                  title={p.full_name || p.email}
                >
                  {initialsOf(p.full_name || p.email)}
                  <span
                    aria-hidden="true"
                    className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white"
                    style={{ background: meta.dot, boxShadow: meta.pulse ? `0 0 0 3px ${meta.dot}33` : "none" }}
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-[12.5px] font-semibold truncate" style={{ color: LIGHT.textPrimary }}>
                      {p.full_name || p.email}
                    </p>
                    {p.is_internal && (
                      <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-1 h-3.5 rounded" style={{ background: "#EEF2FF", color: "#6366F1" }}>
                        Team
                      </span>
                    )}
                    {solSig && solSig.state !== "Responsive" ? (
                      <SolicitorResponsivenessPill
                        state={solSig.state}
                        reason={solSig.reasons?.[0] || null}
                        size="xs"
                        testid={`participants-solicitor-responsiveness-${p.user_id || p.id || i}`}
                      />
                    ) : null}
                  </div>
                  <p className="text-[10.5px] truncate" style={{ color: LIGHT.textSubtle }}>
                    {p.role_label || p.role || "Participant"}
                    {p.assigned_open_tasks > 0 && (
                      <>
                        {" · "}
                        <span style={{ color: "#A16207" }}>{p.assigned_open_tasks} open</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="flex flex-col items-end shrink-0 gap-0.5">
                  <ResponsivenessPill responsiveness={p.responsiveness} />
                  <span className="text-[9.5px] tabular-nums" style={{ color: LIGHT.textSubtle }}>
                    {p.responsiveness === "active" ? "online" : `seen ${fmtRel(p.last_seen_at)}`}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {participants.length > 6 && (
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${LIGHT.border}` }}>
          <button
            type="button"
            onClick={onInvite}
            className="text-[11.5px] font-semibold inline-flex items-center gap-1"
            style={{ color: "#6366F1" }}
            data-testid="dealroom-view-all-participants"
          >
            View all ({participants.length}) <ArrowRight size={11} />
          </button>
        </div>
      )}
    </section>
  );
};

/* ─── Document Requests card (request/upload/approve workflow) ───── */
const STATUS_META = {
  requested: { bg: "#FEF3C7", fg: "#A16207", border: "#FDE68A", label: "Requested" },
  uploaded:  { bg: "#DBEAFE", fg: "#1D4ED8", border: "#BFDBFE", label: "Uploaded · Review" },
  approved:  { bg: "#DCFCE7", fg: "#15803D", border: "#BBF7D0", label: "Approved" },
  rejected:  { bg: "#FEE2E2", fg: "#B91C1C", border: "#FECACA", label: "Rejected" },
  cancelled: { bg: "#F1F5F9", fg: "#64748B", border: "#E2E8F0", label: "Cancelled" },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.requested;
  return (
    <span
      className="inline-flex items-center h-5 px-1.5 rounded text-[9.5px] font-bold uppercase tracking-[0.12em]"
      style={{ background: m.bg, color: m.fg, border: `1px solid ${m.border}` }}
    >
      {m.label}
    </span>
  );
};

export const DocumentRequestsCard = ({
  requests = [],
  documents = [],
  onCreate, onApprove, onReject, onFulfill, onCancel, onDelete,
}) => {
  const open = requests.filter((r) => r.status === "requested" || r.status === "uploaded");
  const closed = requests.filter((r) => r.status === "approved");
  const [showAdd, setShowAdd] = React.useState(false);
  const [fulfillingId, setFulfillingId] = React.useState(null);

  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-doc-requests">
      <SectionTitle
        action={
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="text-[10.5px] font-semibold inline-flex items-center gap-0.5"
            style={{ color: "#6366F1" }}
            data-testid="dealroom-doc-request-add"
          >
            <Plus size={10} /> Request
          </button>
        }
      >
        Document Requests
      </SectionTitle>
      {requests.length === 0 ? (
        <div className="py-5 text-center">
          <p className="text-[11.5px]" style={{ color: LIGHT.textSubtle }}>No document requests yet.</p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="mt-2 h-8 px-3 rounded-md text-[11.5px] font-semibold inline-flex items-center gap-1"
            style={{ background: "#EEF2FF", color: "#6366F1", border: "1px solid rgba(99,102,241,0.20)" }}
          >
            <Plus size={11} /> Request first document
          </button>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {[...open, ...closed].slice(0, 8).map((r) => (
            <li
              key={r.id}
              className="rounded-lg p-2.5"
              style={{ background: "#FAFAFA", border: `1px solid ${LIGHT.border}` }}
              data-testid={`dealroom-doc-request-${r.id}`}
            >
              <div className="flex items-start gap-2.5">
                <span
                  className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: STATUS_META[r.status]?.bg || "#F1F5F9", color: STATUS_META[r.status]?.fg || "#475569" }}
                >
                  <FileText size={11} strokeWidth={2.4} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <p className="text-[12px] font-semibold truncate" style={{ color: LIGHT.textPrimary }}>{r.label}</p>
                    <StatusPill status={r.status} />
                  </div>
                  <p className="text-[10.5px] truncate" style={{ color: LIGHT.textSubtle }}>
                    From: <span style={{ color: LIGHT.textMuted }}>{r.requested_from_label || r.requested_from || "Anyone"}</span>
                    {r.requested_at && <> · {fmtRel(r.requested_at)}</>}
                    {r.document_name && <> · <span style={{ color: LIGHT.textMuted }}>{r.document_name}</span></>}
                  </p>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {r.status === "requested" && (
                      <>
                        <button
                          type="button"
                          onClick={() => setFulfillingId(fulfillingId === r.id ? null : r.id)}
                          className="h-6 px-2 rounded text-[10.5px] font-semibold inline-flex items-center gap-1"
                          style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
                          data-testid={`dealroom-doc-request-link-${r.id}`}
                        >
                          <Upload size={9} /> Link upload
                        </button>
                        <button
                          type="button"
                          onClick={() => onCancel?.(r)}
                          className="h-6 px-2 rounded text-[10.5px] font-semibold"
                          style={{ background: "transparent", color: LIGHT.textSubtle }}
                          data-testid={`dealroom-doc-request-cancel-${r.id}`}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {r.status === "uploaded" && (
                      <>
                        <button
                          type="button"
                          onClick={() => onApprove?.(r)}
                          className="h-6 px-2 rounded text-[10.5px] font-semibold text-white inline-flex items-center gap-1"
                          style={{ background: "linear-gradient(135deg, #10B981, #15803D)" }}
                          data-testid={`dealroom-doc-request-approve-${r.id}`}
                        >
                          <ThumbsUp size={9} /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => onReject?.(r)}
                          className="h-6 px-2 rounded text-[10.5px] font-semibold inline-flex items-center gap-1"
                          style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: "#B91C1C" }}
                          data-testid={`dealroom-doc-request-reject-${r.id}`}
                        >
                          <RotateCcw size={9} /> Reject
                        </button>
                      </>
                    )}
                    {(r.status === "approved" || r.status === "cancelled") && (
                      <button
                        type="button"
                        onClick={() => onDelete?.(r)}
                        className="h-6 px-2 rounded text-[10.5px] font-semibold inline-flex items-center gap-1"
                        style={{ background: "transparent", color: LIGHT.textSubtle }}
                        data-testid={`dealroom-doc-request-delete-${r.id}`}
                      >
                        <Trash2 size={9} /> Remove
                      </button>
                    )}
                    {r.status === "rejected" && (
                      <button
                        type="button"
                        onClick={() => setFulfillingId(fulfillingId === r.id ? null : r.id)}
                        className="h-6 px-2 rounded text-[10.5px] font-semibold inline-flex items-center gap-1"
                        style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
                        data-testid={`dealroom-doc-request-resubmit-${r.id}`}
                      >
                        <Upload size={9} /> Re-submit
                      </button>
                    )}
                  </div>

                  {fulfillingId === r.id && (
                    <FulfillPicker
                      documents={documents}
                      onPick={(docId) => {
                        onFulfill?.(r, docId);
                        setFulfillingId(null);
                      }}
                      onClose={() => setFulfillingId(null)}
                    />
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <AddRequestForm
          onCreate={(payload) => {
            onCreate?.(payload);
            setShowAdd(false);
          }}
          onClose={() => setShowAdd(false)}
        />
      )}
    </section>
  );
};

const FulfillPicker = ({ documents, onPick, onClose }) => {
  return (
    <div
      className="mt-2 rounded-md p-2"
      style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}` }}
      data-testid="dealroom-fulfill-picker"
    >
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: LIGHT.textSubtle }}>
          Pick uploaded document
        </p>
        <button type="button" onClick={onClose} className="text-[10px]" style={{ color: LIGHT.textSubtle }}>
          <X size={10} />
        </button>
      </div>
      {documents.length === 0 ? (
        <p className="text-[10.5px] py-1.5" style={{ color: LIGHT.textSubtle }}>
          Upload a file in the Documents tab first.
        </p>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto cf-scroll">
          {documents.slice(0, 8).map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onPick(d.id)}
                className="w-full text-left h-7 px-2 rounded text-[11px] font-semibold inline-flex items-center gap-1.5 hover:bg-slate-100 transition-colors"
                style={{ color: LIGHT.textPrimary }}
                data-testid={`dealroom-fulfill-pick-${d.id}`}
              >
                <FileText size={10} style={{ color: "#6366F1" }} />
                <span className="truncate">{d.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CANONICAL = [
  { id: "ta6", label: "TA6 Property Information" },
  { id: "ta10", label: "TA10 Fittings & Contents" },
  { id: "memo", label: "Memorandum of Sale" },
  { id: "pof", label: "Proof of Funds" },
  { id: "id", label: "ID Verification" },
  { id: "mortgage", label: "Mortgage Offer" },
  { id: "leasehold", label: "Leasehold / Management Pack" },
  { id: "searches", label: "Local Searches" },
  { id: "contracts", label: "Draft Contract" },
  { id: "survey", label: "Survey Report" },
  { id: "epc", label: "EPC Certificate" },
  { id: "deposit", label: "Deposit Receipt" },
];

const ROLES = [
  { id: "buyer", label: "Buyer" },
  { id: "seller", label: "Seller" },
  { id: "buyer_solicitor", label: "Buyer's Solicitor" },
  { id: "seller_solicitor", label: "Seller's Solicitor" },
  { id: "broker", label: "Mortgage Broker" },
];

const AddRequestForm = ({ onCreate, onClose }) => {
  const [docType, setDocType] = React.useState("ta6");
  const [from, setFrom] = React.useState("");
  const [note, setNote] = React.useState("");

  const submit = (e) => {
    e?.preventDefault();
    onCreate({
      doc_type: docType,
      requested_from: from || undefined,
      note: note || undefined,
    });
  };

  return (
    <form
      onSubmit={submit}
      className="mt-3 rounded-lg p-3 space-y-2"
      style={{ background: "#F8FAFC", border: `1px solid ${LIGHT.border}` }}
      data-testid="dealroom-doc-request-form"
    >
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: LIGHT.textSubtle }}>Document</label>
        <div className="mt-1">
          <PremiumSelect
            value={docType}
            onChange={setDocType}
            options={CANONICAL.map((c) => ({ value: c.id, label: c.label }))}
            size="sm"
            searchable
            ariaLabel="Document type"
            testid="dealroom-doc-request-type"
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: LIGHT.textSubtle }}>From (optional)</label>
        <div className="mt-1">
          <PremiumSelect
            value={from}
            onChange={(v) => setFrom(v || "")}
            options={[
              { value: "", label: "Auto · use canonical owner" },
              ...ROLES.map((r) => ({ value: r.id, label: r.label })),
            ]}
            size="sm"
            placeholder="Auto · use canonical owner"
            clearable
            ariaLabel="Requested from role"
            testid="dealroom-doc-request-from"
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: LIGHT.textSubtle }}>Note (optional)</label>
        <div className="mt-1">
          <PremiumInput
            value={note}
            onChange={setNote}
            maxLength={200}
            placeholder="e.g. Need this before exchange"
            size="sm"
            testid="dealroom-doc-request-note"
          />
        </div>
      </div>
      <div className="flex items-center gap-1.5 pt-1">
        <button
          type="submit"
          className="h-8 px-3 rounded-md text-[11.5px] font-bold text-white"
          style={{ background: "linear-gradient(135deg, #8B5CF6, #6366F1)" }}
          data-testid="dealroom-doc-request-submit"
        >
          Send request
        </button>
        <button
          type="button"
          onClick={onClose}
          className="h-8 px-3 rounded-md text-[11.5px] font-semibold"
          style={{ color: LIGHT.textMuted }}
          data-testid="dealroom-doc-request-cancel-form"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

/* ─── Document Requests panel (full Documents tab content) ─────── */
export const DocumentRequestsPanel = ({
  requests = [],
  documents = [],
  onCreate, onApprove, onReject, onFulfill, onCancel, onDelete, onUpload,
  onDownloadDoc, onDeleteDoc,
}) => {
  const counts = {
    requested: requests.filter((r) => r.status === "requested").length,
    uploaded:  requests.filter((r) => r.status === "uploaded").length,
    approved:  requests.filter((r) => r.status === "approved").length,
  };
  const [showAdd, setShowAdd] = React.useState(false);

  return (
    <div className="space-y-4" data-testid="dealroom-doc-panel">
      <div className="rounded-2xl p-5" style={cardStyle}>
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="text-[15px] font-bold" style={{ color: LIGHT.textPrimary }}>Operational Documents</h3>
            <p className="text-[11.5px] mt-1" style={{ color: LIGHT.textMuted }}>
              Track who owes what — request, review, approve. Each action posts to Flow.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onUpload}
              className="h-9 px-3 rounded-md text-[12px] font-semibold inline-flex items-center gap-1.5"
              style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
              data-testid="dealroom-doc-panel-upload"
            >
              <Upload size={11} /> Upload
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="h-9 px-3 rounded-md text-white text-[12px] font-bold inline-flex items-center gap-1.5"
              style={{ background: "linear-gradient(135deg, #8B5CF6, #6366F1)" }}
              data-testid="dealroom-doc-panel-request"
            >
              <Plus size={11} /> Request document
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat label="Requested" value={counts.requested} tone="warn" />
          <Stat label="Awaiting review" value={counts.uploaded} tone="info" />
          <Stat label="Approved" value={counts.approved} tone="ok" />
        </div>

        {showAdd && (
          <AddRequestForm
            onCreate={(payload) => {
              onCreate?.(payload);
              setShowAdd(false);
            }}
            onClose={() => setShowAdd(false)}
          />
        )}

        {requests.length === 0 ? (
          <div className="py-8 text-center">
            <span
              className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center mb-3"
              style={{ background: "#EEF2FF", color: "#6366F1" }}
            >
              <FileText size={18} />
            </span>
            <p className="text-[13px] font-bold" style={{ color: LIGHT.textPrimary }}>No document requests yet</p>
            <p className="text-[11.5px] mt-1 max-w-md mx-auto" style={{ color: LIGHT.textMuted }}>
              Get the operational docs flowing — request the TA6, Proof of Funds, or Mortgage Offer to start the chain.
            </p>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="mt-3 h-9 px-3 rounded-md text-[12px] font-bold inline-flex items-center gap-1.5"
              style={{ background: "#EEF2FF", color: "#6366F1", border: "1px solid rgba(99,102,241,0.20)" }}
            >
              <Plus size={11} /> Request first document
            </button>
          </div>
        ) : (
          <ul className="space-y-2">
            {requests.map((r) => (
              <RequestRow
                key={r.id}
                request={r}
                documents={documents}
                onApprove={onApprove}
                onReject={onReject}
                onFulfill={onFulfill}
                onCancel={onCancel}
                onDelete={onDelete}
              />
            ))}
          </ul>
        )}
      </div>

      {/* All uploaded files reference list */}
      <div className="rounded-2xl p-5" style={cardStyle}>
        <h3 className="text-[14px] font-bold mb-3" style={{ color: LIGHT.textPrimary }}>
          All uploads <span className="text-[11.5px] font-semibold" style={{ color: LIGHT.textMuted }}>· {documents.length}</span>
        </h3>
        {documents.length === 0 ? (
          <p className="text-[11.5px] py-3" style={{ color: LIGHT.textSubtle }}>No files uploaded yet.</p>
        ) : (
          <ul className="divide-y" style={{ borderColor: LIGHT.border }}>
            {documents.map((d) => (
              <li key={d.id} className="flex items-center gap-3 py-2.5" data-testid={`dealroom-upload-${d.id}`}>
                <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#EEF2FF", color: "#6366F1" }}>
                  <FileText size={13} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-semibold truncate" style={{ color: LIGHT.textPrimary }}>{d.name}</p>
                  <p className="text-[10.5px]" style={{ color: LIGHT.textSubtle }}>
                    {d.uploaded_by_name || "—"} · {d.created_at ? fmtRel(d.created_at) : "—"}
                    {d.size_bytes ? ` · ${(d.size_bytes / 1024).toFixed(0)} KB` : ""}
                  </p>
                </div>
                {onDownloadDoc && (
                  <button
                    type="button"
                    onClick={() => onDownloadDoc(d)}
                    className="text-[11px] font-semibold px-2.5 h-7 rounded-md"
                    style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
                    data-testid={`dealroom-upload-download-${d.id}`}
                    title="Download"
                  >
                    Download
                  </button>
                )}
                {onDeleteDoc && (
                  <button
                    type="button"
                    onClick={() => onDeleteDoc(d)}
                    className="text-[11px] font-semibold px-2.5 h-7 rounded-md"
                    style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: "#B91C1C" }}
                    data-testid={`dealroom-upload-delete-${d.id}`}
                    title="Remove"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, tone }) => {
  const palette = {
    warn: { bg: "#FFF7ED", fg: "#C2410C" },
    info: { bg: "#EFF6FF", fg: "#1D4ED8" },
    ok:   { bg: "#ECFDF5", fg: "#15803D" },
  }[tone] || { bg: "#F1F5F9", fg: "#475569" };
  return (
    <div className="rounded-lg px-3 py-2.5" style={{ background: palette.bg }}>
      <p className="text-[9.5px] font-bold uppercase tracking-[0.14em]" style={{ color: palette.fg, opacity: 0.8 }}>{label}</p>
      <p className="text-[20px] font-bold tabular-nums mt-0.5" style={{ color: palette.fg }}>{value}</p>
    </div>
  );
};

const RequestRow = ({ request: r, documents, onApprove, onReject, onFulfill, onCancel, onDelete }) => {
  const [picking, setPicking] = React.useState(false);
  return (
    <li
      className="rounded-lg p-3 flex items-start gap-3"
      style={{ background: "#FAFAFA", border: `1px solid ${LIGHT.border}` }}
      data-testid={`dealroom-doc-row-${r.id}`}
    >
      <span
        className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: STATUS_META[r.status]?.bg || "#F1F5F9", color: STATUS_META[r.status]?.fg || "#475569" }}
      >
        <FileText size={13} strokeWidth={2.4} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[13px] font-bold" style={{ color: LIGHT.textPrimary }}>{r.label}</p>
          <StatusPill status={r.status} />
        </div>
        <p className="text-[11px] mt-1" style={{ color: LIGHT.textMuted }}>
          From: <span style={{ color: LIGHT.textPrimary }}>{r.requested_from_label || r.requested_from || "Anyone"}</span>
          {" · "}
          Requested by {r.requested_by_name || "—"} {fmtRel(r.requested_at)}
          {r.document_name && (
            <>
              {" · "}
              <span style={{ color: LIGHT.textPrimary }}>{r.document_name}</span>
            </>
          )}
          {r.approved_by_name && r.status === "approved" && (
            <> {" · "} Approved by {r.approved_by_name} {fmtRel(r.approved_at)}</>
          )}
        </p>
        {r.note && (
          <p className="text-[11.5px] mt-1.5 italic" style={{ color: LIGHT.textMuted }}>"{r.note}"</p>
        )}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {r.status === "requested" && (
            <>
              <button
                type="button"
                onClick={() => setPicking(!picking)}
                className="h-7 px-2.5 rounded text-[11px] font-semibold inline-flex items-center gap-1"
                style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
                data-testid={`dealroom-doc-row-link-${r.id}`}
              >
                <Upload size={10} /> Link upload
              </button>
              <button
                type="button"
                onClick={() => onCancel?.(r)}
                className="h-7 px-2.5 rounded text-[11px] font-semibold"
                style={{ color: LIGHT.textSubtle }}
                data-testid={`dealroom-doc-row-cancel-${r.id}`}
              >
                Cancel request
              </button>
            </>
          )}
          {r.status === "uploaded" && (
            <>
              <button
                type="button"
                onClick={() => onApprove?.(r)}
                className="h-7 px-2.5 rounded text-[11px] font-bold text-white inline-flex items-center gap-1"
                style={{ background: "linear-gradient(135deg, #10B981, #15803D)" }}
                data-testid={`dealroom-doc-row-approve-${r.id}`}
              >
                <ThumbsUp size={10} /> Approve
              </button>
              <button
                type="button"
                onClick={() => onReject?.(r)}
                className="h-7 px-2.5 rounded text-[11px] font-semibold inline-flex items-center gap-1"
                style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: "#B91C1C" }}
                data-testid={`dealroom-doc-row-reject-${r.id}`}
              >
                <RotateCcw size={10} /> Reject
              </button>
            </>
          )}
          {r.status === "rejected" && (
            <button
              type="button"
              onClick={() => setPicking(!picking)}
              className="h-7 px-2.5 rounded text-[11px] font-semibold inline-flex items-center gap-1"
              style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
              data-testid={`dealroom-doc-row-resubmit-${r.id}`}
            >
              <Upload size={10} /> Re-submit
            </button>
          )}
          {(r.status === "approved" || r.status === "cancelled") && (
            <button
              type="button"
              onClick={() => onDelete?.(r)}
              className="h-7 px-2.5 rounded text-[11px] font-semibold inline-flex items-center gap-1"
              style={{ color: LIGHT.textSubtle }}
              data-testid={`dealroom-doc-row-delete-${r.id}`}
            >
              <Trash2 size={10} /> Remove
            </button>
          )}
        </div>
        {picking && (
          <FulfillPicker
            documents={documents}
            onPick={(docId) => {
              onFulfill?.(r, docId);
              setPicking(false);
            }}
            onClose={() => setPicking(false)}
          />
        )}
      </div>
    </li>
  );
};
